import { useIntersectionObserver } from "@vueuse/core";
import { ref, watch } from "vue";

import type { ComputedRef, MaybeRefOrGetter, Ref } from "vue";

interface UseInfiniteScrollSentinelOptions {
  sentinel: Ref<HTMLElement | null>;
  canLoadMore: Ref<boolean> | ComputedRef<boolean>;
  isLoading: Ref<boolean> | ComputedRef<boolean>;
  onLoadMore: () => void | Promise<void>;
  root?: MaybeRefOrGetter<HTMLElement | null>;
  rootMargin?: string;
}

const useInfiniteScrollSentinel = ({
  sentinel,
  canLoadMore,
  isLoading,
  onLoadMore,
  root,
  rootMargin = "50px",
}: UseInfiniteScrollSentinelOptions) => {
  const isIntersecting = ref(false);

  const triggerLoad = () => {
    if (isIntersecting.value && canLoadMore.value && !isLoading.value) {
      onLoadMore();
    }
  };

  const { pause, resume } = useIntersectionObserver(
    sentinel,
    ([entry]) => {
      const visible = !!entry?.isIntersecting;

      if (visible !== isIntersecting.value) {
        isIntersecting.value = visible;
      }

      triggerLoad();
    },
    {
      root,
      rootMargin: `0px 0px ${rootMargin} 0px`,
      threshold: 0,
    }
  );

  watch(isLoading, () => triggerLoad());

  watch(
    canLoadMore,
    (value) => {
      if (value) {
        resume();
      } else {
        pause();
      }
    },
    { immediate: true }
  );
};

export { useInfiniteScrollSentinel };
